import { useCallback, useState } from 'react';
import { Alert, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import type { APIKeyMetadata } from 'api-client';
import { useAppContext } from '@/context';
import APIKeyManagement from './APIKeyManagement';
import { profileFailure, useProfileResource } from './profileRequests';

export default function APIKeysSection() {
  const { apiClients } = useAppContext();
  const load = useCallback(() => apiClients.users.usersProfileApiKeysList(), [apiClients.users]);
  const resource = useProfileResource(load, 'Could not load API keys. Please retry.');
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<string | null>(null);
  const action = async (run: () => Promise<void>): Promise<boolean> => {
    if (pending || !resource.data) return false;
    const current = resource.generation.current;
    setPending(true);
    setError(null);
    setSuccess(null);
    try {
      await run();
      return current === resource.generation.current;
    } catch (failure) {
      const message = await profileFailure(failure, 'Could not complete the API key action. Please retry.');
      if (current === resource.generation.current) setError(message);
      return false;
    } finally {
      if (current === resource.generation.current) setPending(false);
    }
  };
  const create = () => {
    const current = resource.generation.current;
    void action(async () => {
      const result = await apiClients.users.usersProfileApiKeyCreate();
      if (current !== resource.generation.current) return;
      // Do not risk losing a once-only secret to a subsequent list fetch.
      setNewKey(result.key);
      resource.setData((data) => (data ? { apiKeys: [...data.apiKeys, result.apiKey] } : data));
      setSuccess(result.message);
    });
  };
  const revoke = (key: APIKeyMetadata) => {
    const current = resource.generation.current;
    return action(async () => {
      const result = await apiClients.users.usersProfileApiKeyRevoke({ apiKeyRevoke: { keyId: key.id } });
      if (current !== resource.generation.current) return;
      resource.setData((data) => (data ? { apiKeys: data.apiKeys.filter((value) => value.id !== key.id) } : data));
      setSuccess(result.message);
    });
  };
  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" component="h2">
            API Keys
          </Typography>
          {resource.loading && <CircularProgress aria-label="Loading API keys" />}
          {resource.error && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" onClick={resource.retry}>
                  Retry
                </Button>
              }
            >
              {resource.error}
            </Alert>
          )}
          {resource.data && (
            <APIKeyManagement
              keys={resource.data.apiKeys}
              pending={pending}
              error={error}
              success={success}
              newKey={newKey}
              onCreate={create}
              onRevoke={revoke}
              onDismissKey={() => setNewKey(null)}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
